import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {Box, Button, Grid, TextField} from "@mui/material";
import styled from "styled-components";
import theme from "../../theme/theme";
import axios from "axios";
import {parseError} from "../../services/http.service";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {useUser} from "../../core/UserContext";
import {ActionButton, CancelButton} from "../common/ActionButtons";
import {AvatarContainer, ButtonGroup, StyledAvatar} from "../../pages/user-settings/UserSettings.styled";
import {useTranslation} from 'react-i18next';
import AvatarCropDialog from "../dialogs/AvatarCropDialog";
import FieldHelpSuffix from "../common/FieldHelpSuffix";

const StyledTextField = styled(TextField)`
    width: 100%;
    max-width: 550px;
`;

const StyledAvatarButton = styled(Button)`
    border: none !important;
    color: black !important;
    font-weight: 400 !important;
    font-size: 15px !important;
    width: max-content !important;
    margin-bottom: 16px !important;

    @media (min-width: 960px) {
        margin-bottom: 0 !important;
    }
`;

const ResetAvatarButton = styled(Button)`
    position: absolute !important;
    top: 0;
    right: 0;
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    color: ${theme.palette.primary.main} !important;
    border: 1px solid ${theme.palette.primary.main} !important;
    font-weight: bold !important;
    font-size: 12px !important;
    padding: 0;
    z-index: 10;
    transform: translate(50%, -50%);
`;

const MAX_SIZE = 1024 * 1024;

const UserData: React.FC = () => {
    const { t } = useTranslation();
    const {user, fetchUserData} = useUser();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [position, setPosition] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [avatar, setAvatar] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
    const [isAvatarChanged, setIsAvatarChanged] = useState<boolean>(false);
    const [cropDialogOpen, setCropDialogOpen] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const normalize = (v?: string | null) => (v ?? "").trim();

    useEffect(() => {
        if (!user) {
            fetchUserData();
        } else {
            setFirstName(user.firstName || user.name || "");
            setLastName(user.lastName || user.surname || "");
            setPosition(user.position || "");
            setPhoneNumber(user.phoneNumber || "");
        }
    }, [user, fetchUserData]);

    useEffect(() => {
        if (user) {
            const profileChanged = (firstName !== (user.firstName || user.name) && firstName !== "") ||
                (lastName !== (user.lastName || user.surname) && lastName !== "") ||
                (normalize(position) !== normalize(user.position)) ||
                (normalize(phoneNumber) !== normalize(user.phoneNumber));

            setIsFormChanged(profileChanged || isAvatarChanged);
        }
    }, [firstName, lastName, position, phoneNumber, user, isAvatarChanged]);

    const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > MAX_SIZE) {
                showSnackbar(t('userData.fileTooLarge'), "error");
                setAvatar(null);
                event.target.value = "";
                return;
            }

            setSelectedFile(file);
            setCropDialogOpen(true);
        }
    };

    const handleCropSave = (croppedBlob: Blob) => {
        const croppedFile = new File([croppedBlob], selectedFile?.name || 'avatar.png', {
            type: 'image/png'
        });
        
        setAvatar(croppedFile);
        setAvatarPreview(URL.createObjectURL(croppedBlob));
        setIsAvatarChanged(true);
        
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleCropClose = () => {
        setCropDialogOpen(false);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleAvatarUpload = async (fileToUpload?: File) => {
        const file = fileToUpload || avatar;
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append("avatar", file);

            await axios.patch("/auth/avatar", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            showSnackbar(t('userData.avatarUpdated'), "success");
            fetchUserData();
        } catch (error) {
            const parsedError = parseError(error);
            showSnackbar(parsedError, "error");
        }
    };

    const resetAvatar = () => {
        setAvatar(null);
        setAvatarPreview(null);
        setIsAvatarChanged(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSaveChanges = async () => {
        try {
            const profileData: { firstName?: string; lastName?: string; position?: string; phoneNumber?: string } = {};

            if (firstName !== (user?.firstName || user?.name) && firstName !== "") {
                profileData.firstName = firstName;
            }

            if (lastName !== (user?.lastName || user?.surname) && lastName !== "") {
                profileData.lastName = lastName;
            }

            if (position !== user?.position) {
                profileData.position = position;
            }

            if (phoneNumber !== user?.phoneNumber) {
                profileData.phoneNumber = phoneNumber;
            }

            if (Object.keys(profileData).length > 0) {
                await axios.patch("/auth/profile", profileData);
                showSnackbar(t('userData.profileUpdated'), "success");
            }

            if (isAvatarChanged && avatar) {
                await handleAvatarUpload(avatar);
                setIsAvatarChanged(false);
            }

            fetchUserData();
        } catch (error) {
            const parsedError = parseError(error);
            showSnackbar(parsedError, "error");
        }
    };

    if (!user) {
        return null;
    }

    const userFirstName = user.firstName || user.name || "";
    const userLastName = user.lastName || user.surname || "";
    const userEmail = user.email || "";
    const userInitials = userFirstName && userLastName
        ? `${userFirstName.charAt(0)}${userLastName.charAt(0)}`
        : (userFirstName
            ? userFirstName.charAt(0)
            : (userEmail ? userEmail.charAt(0).toUpperCase() : '?'));

    return (
        <Box sx={{
            paddingTop: { xs: '24px', md: '48px' },
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            <Box sx={{flex: 1}}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <AvatarContainer style={{position: 'relative'}}>
                            {avatarPreview || user.avatarBase64 ? (
                                <StyledAvatar
                                    src={avatarPreview || user.avatarBase64}
                                    alt={userFirstName || t('userData.userAvatar')}
                                    style={{border: '1px solid #e4e4e4'}}
                                />
                            ) : (
                                <StyledAvatar
                                    sx={{
                                        bgcolor: '#1976d2',
                                        fontSize: '3rem',
                                        fontWeight: 'bold',
                                        width: '150px',
                                        height: '150px'
                                    }}
                                    style={{border: '1px solid #e4e4e4'}}
                                >
                                    {userInitials}
                                </StyledAvatar>
                            )}
                            {isAvatarChanged && (
                                <ResetAvatarButton onClick={resetAvatar}>
                                    X
                                </ResetAvatarButton>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                style={{display: "none"}}
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                            />
                            <StyledAvatarButton
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t('userData.changeProfilePhoto')}
                            </StyledAvatarButton>
                        </AvatarContainer>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.email')}
                                value={userEmail}
                                disabled
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.email')}
                                            ariaLabel={t('userData.help.email') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.firstName')}
                                value={firstName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.firstName')}
                                            ariaLabel={t('userData.help.firstName') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.lastName')}
                                value={lastName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.lastName')}
                                            ariaLabel={t('userData.help.lastName') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.position')}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.position')}
                                            ariaLabel={t('userData.help.position') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.phoneNumber')}
                                type="tel"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "^\\+?[0-9]*$",
                                    maxLength: 15
                                }}
                                value={phoneNumber}
                                onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^+\d]/g, "");
                                    setPhoneNumber(cleaned);
                                }}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.phoneNumber')}
                                            ariaLabel={t('userData.help.phoneNumber') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <AvatarCropDialog
                open={cropDialogOpen}
                imageFile={selectedFile}
                onClose={handleCropClose}
                onSave={handleCropSave}
            />

            <ButtonGroup>
                <CancelButton onClick={() => navigate(-1)}>
                    {t('common.cancel')}
                </CancelButton>
                <ActionButton
                    onClick={handleSaveChanges}
                    disabled={!isFormChanged && !isAvatarChanged}
                >
                    {t('common.saveChanges')}
                </ActionButton>
            </ButtonGroup>
        </Box>
    );
};

export default UserData;
